import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';
import { Box } from '@mui/system';
import { FORM_ERROR } from 'final-form';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ReplayCircleFilledIcon from '@mui/icons-material/ReplayCircleFilled';
import ConfirmationDialog from '@components/ConfirmationDialog';
import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';
import {
  getPaymentConfig,
  addPaymentConfig,
  updatePaymentConfig,
  deletePaymentConfig,
  invalidateCache,
} from '../store/payment-config';
import ApplicationPageTemplate from '../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import TableToolbar from '../../../components/TableToolbar';
import { viewConfigs } from '../payment-configuration.breadcrumbs';

function ConfigToolbar({
  setNewConfigDialogOpen,
  confirmRefreshHandler,
  permissions = {},
}) {
  return (
    <TableToolbar>
      <div>
        <Button
          variant="outlined"
          disabled={!permissions?.canRefreshPaymentConfigCache}
          onClick={() => confirmRefreshHandler()}
          startIcon={<ReplayCircleFilledIcon />}
          style={{ marginRight: 10 }}>
          Refresh Cache
        </Button>
        <Button
          disabled={!permissions?.canCreatePaymentConfig}
          variant="contained"
          onClick={() => setNewConfigDialogOpen(true)}
          startIcon={<AddIcon />}>
          New
        </Button>
      </div>
    </TableToolbar>
  );
}

export default function PaymentConfigList({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const configList = useSelector((state) => state.paymentConfig.list);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false });
  const [showRefreshCacheConfirmation, setShowRefreshCacheConfirmation] =
    useState({ show: false });
  const [toDeleteConfig, setToDeleteConfig] = useState('');
  const [newConfigDialogOpen, setNewConfigDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    if (!permissions?.canViewPaymentConfig) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewConfigs));
  }, [dispatch, navigate]);

  const deleteHandler = (id) => {
    setToDeleteConfig(id);
    setShowDeleteModal({ show: true });
  };

  const yesDeleteHandler = (id) => {
    dispatch(deletePaymentConfig({ id })).then((response) => {
      const { payload } = response;
      if (payload && payload.status === 'OK') {
        dispatch(showSuccessMessage('Config Deleted Successfully'));
      } else {
        dispatch(
          showErrorMessage({ message: payload.message, duration: 4000 }),
        );
      }
    });
  };

  const confirmRefreshHandler = () => {
    setShowRefreshCacheConfirmation({ show: true });
  };

  const yesConfirmRefreshHandler = () => {
    dispatch(invalidateCache()).then(({ payload }) => {
      if (payload && payload.status === 'OK') {
        dispatch(showSuccessMessage('Successfully Triggered Refresh'));
      } else {
        dispatch(
          showErrorMessage({ message: payload.message, duration: 4000 }),
        );
      }
    });
  };

  const resetForm = useCallback(() => {
    setNewConfigDialogOpen(false);
    setFormValues({});
    setIsEditMode(false);
  }, []);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(getPaymentConfig()).then(() => {
      setIsFetchingData(false);
    });
  }, [dispatch]);

  const createNewConfig = (values) => {
    const { featureName, value } = values;
    const payload = isEditMode
      ? {
          featureName,
          value,
        }
      : values;

    return dispatch(
      isEditMode
        ? updatePaymentConfig({
            payload,
            id: values.id,
          })
        : addPaymentConfig({
            payload,
          }),
    ).then(({ payload: resp }) => {
      if (
        resp &&
        resp.data &&
        (resp.status === 'Created' || resp.status === 'OK')
      ) {
        dispatch(
          showSuccessMessage(
            `Config ${isEditMode ? 'Updated' : 'Added'} Successfully`,
          ),
        );
        return resetForm();
      }

      return { [FORM_ERROR]: resp.message };
    });
  };

  const validateNewConfig = (values) => {
    const errors = {};
    if (!values.featureName) {
      errors.featureName = 'Feature Name is mandatory and cannot be empty';
    }
    if (!values.value) {
      errors.value = 'Value is mandatory and cannot be empty';
    }
    return errors;
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title="Configurations"
      description="List of non-LOB specific configurations"
      pageReady={!isFetchingData}>
      <DataGrid
        autoHeight
        density="compact"
        rows={configList}
        disableColumnFilter
        disableColumnMenu
        disableColumnSelector
        disableDensitySelector
        disableSelectionOnClick
        pageSize={10}
        rowsPerPageOptions={[5]}
        components={{
          Toolbar: () =>
            ConfigToolbar({
              setNewConfigDialogOpen,
              confirmRefreshHandler,
              permissions,
            }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        onRowClick={({ row }) => {
          setIsEditMode(true);
          setFormValues(row);
          setNewConfigDialogOpen(true);
        }}
        columns={[
          {
            field: 'id',
            headerName: 'ID',
            width: 100,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'featureName',
            headerName: 'Feature Name',
            sortable: false,
            headerAlign: 'left',
            align: 'left',
            flex: 1,
          },
          {
            field: 'value',
            headerName: 'Value',
            sortable: false,
            headerAlign: 'left',
            align: 'left',
            flex: 2,
          },
          {
            field: 'modifiedBy',
            headerName: 'Modified By',
            width: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'modifiedAt',
            headerName: 'Modified At',
            valueGetter: ({ row }) =>
              row.modifiedAt !== '' &&
              moment(row.modifiedAt).format('DD/MM/YYYY - HH:mm'),
            width: 180,
            sortable: false,
            headerAlign: 'center',
            align: 'center',
          },
          {
            field: 'actions',
            type: 'actions',
            getActions: ({ row }) => [
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                key="0"
                disabled={!permissions?.canDeletePaymentConfig}
                onClick={() => deleteHandler(row.id)}
              />,
            ],
          },
        ]}
      />
      <ConfirmationDialog
        openDialog={showDeleteModal}
        yesHandler={yesDeleteHandler}
        data={toDeleteConfig}
        title="Delete Configuration"
        message="Are you sure you want to delete the selected Configuration?"
      />
      <ConfirmationDialog
        openDialog={showRefreshCacheConfirmation}
        yesHandler={yesConfirmRefreshHandler}
        title="Refresh Cache"
        message="Are you sure you want refresh the cache?"
      />
      <Dialog open={newConfigDialogOpen}>
        <DialogTitle>
          {isEditMode
            ? 'Update Existing Configuration'
            : 'Add New Configuration'}
        </DialogTitle>
        <Form
          onSubmit={createNewConfig}
          initialValues={isEditMode ? formValues : {}}
          validate={validateNewConfig}
          render={({ submitError, handleSubmit, values, submitting }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <DialogContent style={{ minHeight: '185px' }}>
                <DialogContentText>
                  Insert the config name that can be referenced from the
                  application level
                </DialogContentText>
                <Box sx={{ marginTop: '10px', minHeight: '80px' }}>
                  <TextField
                    label="Feature Name"
                    name="featureName"
                    required
                    variant="filled"
                    fullWidth
                    disabled={isEditMode}
                  />
                  <TextField
                    label="Value"
                    name="value"
                    required
                    variant="filled"
                    fullWidth
                    style={{ marginTop: 20 }}
                    InputProps={{
                      readOnly: !permissions?.canUpdatePaymentConfig,
                    }}
                  />
                </Box>
                {submitError && (
                  <Alert
                    severity="error"
                    style={{ marginTop: 20 }}>
                    {submitError}
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  variant="text"
                  onClick={resetForm}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={
                    !permissions?.canUpdatePaymentConfig ||
                    !permissions?.canCreatePaymentConfig ||
                    submitting ||
                    (isEditMode && values.value === formValues.value)
                  }>
                  Submit
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </ApplicationPageTemplate>
  );
}
